/**
 * Iter 118 / 125 — Normalización y búsqueda por tokens compartida.
 *
 * Compatible byte-a-byte con `backend/services/search.py`.
 *
 * Reglas:
 *   - Convertir a minúsculas.
 *   - Eliminar espacios iniciales/finales y colapsar espacios múltiples.
 *   - Ignorar acentos y diéresis (NFD + strip diacritics).
 *   - Dividir la consulta en tokens y exigir que TODOS aparezcan (AND),
 *     sin importar el orden.
 *   - Coincidencias parciales dentro de cada token (substring).
 *
 * Ejemplo:
 *   normalizeForSearch("Batería") === "bateria"
 *   normalizeForSearch("  BÄTERIA  ") === "bateria"
 *   normalizeForSearch("ñoño") === "nono"
 *
 *   matchesAllTokens("Placa Base MacBook Pro A2338", "base placa") === true
 *   matchesAllTokens("Laura Martínez García", "garcia laura") === true
 *
 * Se usa en TODOS los buscadores del CRM (Inventario, Clientes, Reparaciones,
 * selectores emergentes, etc.). No implementar lógica alternativa en otros
 * módulos — importar de aquí.
 */
export function normalizeForSearch(text) {
  if (text === null || text === undefined) return "";
  return String(text)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // combining diacritical marks
    .replace(/\s+/g, " ")
    .toLowerCase()
    .trim();
}

/**
 * Divide la consulta en tokens únicos no vacíos tras normalizar.
 */
export function tokenizeQuery(needle) {
  const n = normalizeForSearch(needle);
  if (!n) return [];
  const seen = new Set();
  const out = [];
  for (const tok of n.split(" ")) {
    if (tok && !seen.has(tok)) {
      seen.add(tok);
      out.push(tok);
    }
  }
  return out;
}

/**
 * true si TODOS los tokens de `needle` están contenidos en `haystack`
 * tras normalizar. `haystack` puede ser string o array de strings; en el
 * caso de array se concatenan con espacios antes de comparar.
 *
 * Si `needle` está vacío devuelve true (sin filtro).
 */
export function matchesAllTokens(haystack, needle) {
  const tokens = tokenizeQuery(needle);
  if (tokens.length === 0) return true;
  const hay = normalizeForSearch(
    Array.isArray(haystack) ? haystack.filter(Boolean).join(" ") : haystack
  );
  return tokens.every((tok) => hay.includes(tok));
}

/**
 * Alias legacy. Ahora usa la lógica de tokens (AND) — antes era substring
 * simple. Mantenido para no romper imports existentes.
 */
export function matchesNormalized(haystack, needle) {
  return matchesAllTokens(haystack, needle);
}

/**
 * Comprueba si TODOS los tokens de `needle` aparecen en AL MENOS UNO de los
 * campos (concatenados). Semántica AND por token, OR sobre los campos.
 */
export function matchesAnyField(fields, needle) {
  return matchesAllTokens(fields, needle);
}
